import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../context/AuthContext";
import {
  Home, Settings, Layers, Building2, Star, Phone, FileText, LogOut,
  Plus, Edit2, Trash2, Save, X, Eye, BarChart3, Menu, ChevronLeft,
  Users, MessageSquare, TrendingUp, Award, ChevronUp, ChevronDown,
  Lightbulb, Zap, Shield, Heart, MapPin, Clock, CheckCircle, Globe,
  Handshake, Target, Gem, Key, Leaf, Smile, ThumbsUp, Briefcase
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_elite-realty-40/artifacts/q2vcqxyz_%D8%AA%D8%B5%D9%85%D9%8A%D9%85%20%D8%A8%D8%AF%D9%88%D9%86%20%D8%B9%D9%86%D9%88%D8%A7%D9%86%20-%202026-06-21T092601.962.png";

const tabs = [
  { id: "overview",  label: "نظرة عامة",    Icon: BarChart3 },
  { id: "hero",      label: "البانر",        Icon: Home },
  { id: "stats",     label: "الإحصائيات",   Icon: TrendingUp },
  { id: "services",  label: "الخدمات",      Icon: Layers },
  { id: "projects",  label: "العروض العقارية", Icon: Building2 },
  { id: "plots",     label: "مخططات جازان", Icon: MapPin },
  { id: "values",    label: "القيم",         Icon: Star },
  { id: "contact",   label: "التواصل",      Icon: Phone },
  { id: "footer",    label: "الفوتر",       Icon: FileText },
  { id: "inquiries", label: "الرسائل",      Icon: MessageSquare },
  { id: "users",     label: "المستخدمين",   Icon: Users },
  { id: "settings",  label: "إعدادات الموقع", Icon: Settings },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [moreOpen, setMoreOpen] = useState(false);
  const [summary, setSummary] = useState({});

  useEffect(() => {
    API.get("/admin/summary").then(r => setSummary(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const switchTab = (id) => { setActiveTab(id); setMoreOpen(false); };

  // Bottom nav: 4 pinned + "المزيد"
  const bottomPinned = [
    { id: "overview",  Icon: BarChart3,     label: "الرئيسية" },
    { id: "projects",  Icon: Building2,     label: "العروض العقارية" },
    { id: "inquiries", Icon: MessageSquare, label: "الرسائل",  badge: summary.inquiries },
    { id: "settings",  Icon: Settings,      label: "الإعدادات" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex" dir="rtl">

      {/* ── Desktop Sidebar ── */}
      <aside
        data-testid="admin-sidebar"
        className={`hidden lg:flex bg-[#173269] text-white flex-col transition-all duration-300 ${sidebarOpen ? "w-64" : "w-16"} flex-shrink-0`}
      >
        <div className="p-4 border-b border-white/10 flex items-center gap-3">
          <img src={LOGO} alt="logo" className="h-10 w-auto object-contain" style={{mixBlendMode:"screen"}} />
          {sidebarOpen && (
            <div>
              <p className="text-white font-bold text-sm">الوسيم العقارية</p>
              <p className="text-[#ecc775] text-xs">لوحة التحكم</p>
            </div>
          )}
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              data-testid={`sidebar-tab-${id}`}
              onClick={() => switchTab(id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-all duration-200 ${
                activeTab === id ? "bg-[#ecc775] text-[#173269]" : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Icon size={18} className="flex-shrink-0" />
              {sidebarOpen && <span>{label}</span>}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          {sidebarOpen && <p className="text-white/60 text-xs mb-3 truncate">{user?.email}</p>}
          <button onClick={handleLogout} data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-colors rounded-sm text-sm font-semibold">
            <LogOut size={16} className="flex-shrink-0" />
            {sidebarOpen && "تسجيل الخروج"}
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            {/* Sidebar toggle — desktop only */}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="hidden lg:block text-gray-500 hover:text-[#173269]">
              <Menu size={22} />
            </button>
            {/* Mobile: logo + title */}
            <img src={LOGO} alt="logo" className="h-8 w-auto object-contain lg:hidden" style={{mixBlendMode:"multiply"}} />
            <h1 className="text-[#173269] font-black text-base lg:text-lg">
              {tabs.find(t => t.id === activeTab)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" className="flex items-center gap-1.5 text-[#173269] text-sm font-semibold hover:text-[#ecc775] transition-colors bg-gray-50 px-3 py-1.5 rounded-sm border border-gray-200">
              <Eye size={15} />
              <span className="hidden sm:block">معاينة الموقع</span>
            </a>
            {/* Mobile logout */}
            <button onClick={handleLogout} className="lg:hidden p-2 text-red-400 hover:text-red-600">
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto pb-24 lg:pb-6">
          {activeTab === "overview"  && <OverviewTab summary={summary} />}
          {activeTab === "hero"      && <HeroTab />}
          {activeTab === "stats"     && <StatsTab />}
          {activeTab === "services"  && <ServicesTab />}
          {activeTab === "projects"  && <ProjectsTab />}
          {activeTab === "plots"     && <PlotsTab />}
          {activeTab === "values"    && <ValuesTab />}
          {activeTab === "contact"   && <ContactTab />}
          {activeTab === "footer"    && <FooterTab />}
          {activeTab === "inquiries" && <InquiriesTab />}
          {activeTab === "users"     && <UsersTab />}
          {activeTab === "settings"  && <SiteSettingsTab />}
        </main>
      </div>

      {/* ── Mobile Bottom Navigation ── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-2xl" dir="rtl">
        <div className="flex items-stretch">
          {bottomPinned.map(({ id, Icon, label, badge }) => (
            <button
              key={id}
              data-testid={`sidebar-tab-${id}`}
              onClick={() => switchTab(id)}
              className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-1 relative transition-all duration-200 ${
                activeTab === id ? "text-[#173269]" : "text-gray-400"
              }`}
            >
              <div className="relative">
                <Icon size={22} strokeWidth={activeTab === id ? 2.5 : 1.8} />
                {badge > 0 && (
                  <span className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center">
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-bold">{label}</span>
              {activeTab === id && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#173269] rounded-full" />
              )}
            </button>
          ))}

          {/* More button */}
          <button
            onClick={() => setMoreOpen(true)}
            className={`flex-1 flex flex-col items-center justify-center py-2.5 gap-1 transition-all duration-200 ${
              moreOpen ? "text-[#173269]" : "text-gray-400"
            }`}
          >
            <Menu size={22} strokeWidth={1.8} />
            <span className="text-[10px] font-bold">المزيد</span>
          </button>
        </div>
      </div>

      {/* ── More Sheet (Mobile) ── */}
      {moreOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 z-50" onClick={() => setMoreOpen(false)} />
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl" dir="rtl">
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-4 pb-2">
              <p className="text-[#173269] font-black text-sm px-1 py-2">جميع الأقسام</p>
            </div>
            <div className="grid grid-cols-3 gap-2 px-4 pb-6">
              {tabs.map(({ id, Icon, label }) => (
                <button
                  key={id}
                  data-testid={`sidebar-tab-${id}`}
                  onClick={() => switchTab(id)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                    activeTab === id
                      ? "bg-[#173269] text-white"
                      : "bg-gray-50 text-gray-600 hover:bg-[#173269]/10 hover:text-[#173269]"
                  }`}
                >
                  <Icon size={22} />
                  <span className="text-xs font-bold text-center leading-tight">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

    </div>
  );
};

// ========== Overview ==========
const motivationalPhrases = [
  "النجاح في العقار يبدأ بقرار جريء",
  "كل عقار يُضاف هو فرصة جديدة للعملاء",
  "المحترف يبني الثقة قبل أن يبني المشاريع",
  "الشفافية أساس كل صفقة ناجحة",
  "الوسيم تبني أحلام العملاء حجراً بحجر",
  "إدارة محترفة تعني نتائج استثنائية",
];

const OverviewTab = ({ summary }) => {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [phraseVisible, setPhraseVisible] = useState(true);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Rotating phrases
  useEffect(() => {
    const t = setInterval(() => {
      setPhraseVisible(false);
      setTimeout(() => {
        setPhraseIdx(i => (i + 1) % motivationalPhrases.length);
        setPhraseVisible(true);
      }, 400);
    }, 4000);
    return () => clearInterval(t);
  }, []);

  const adminName = user?.name || user?.email?.split("@")[0] || "المدير";
  const hours = now.getHours();
  const greeting = hours < 12 ? "صباح الخير" : hours < 17 ? "مساء الخير" : "مساء النور";

  const timeStr = now.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  const dateStr = now.toLocaleDateString("ar-SA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  const quickLinks = [
    { label: "البانر",    tab: "hero",      icon: Home,         color: "#173269" },
    { label: "العروض العقارية", tab: "projects",  icon: Building2,    color: "#c9a34e" },
    { label: "المخططات", tab: "plots",     icon: MapPin,       color: "#173269" },
    { label: "الخدمات",  tab: "services",  icon: Layers,       color: "#c9a34e" },
    { label: "الرسائل",  tab: "inquiries", icon: MessageSquare, color: "#173269" },
    { label: "التواصل",  tab: "contact",   icon: Phone,        color: "#c9a34e" },
    { label: "الإعدادات", tab: "settings", icon: Settings,     color: "#173269" },
  ];

  return (
    <div className="space-y-6">

      {/* ── Hero Welcome Card ── */}
      <div className="relative overflow-hidden rounded-sm bg-gradient-to-br from-[#173269] via-[#1a3a7a] to-[#0A1128] p-8 shadow-2xl">
        {/* Decorative circles */}
        <div className="absolute top-0 left-0 w-64 h-64 rounded-full bg-[#ecc775]/5 -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full bg-white/3 translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          {/* Greeting + phrase */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[#ecc775]/60 text-sm font-semibold">{greeting}،</span>
            </div>
            <h2 className="text-white font-black text-3xl mb-3 tracking-tight">{adminName}</h2>
            <div
              className="flex items-center gap-2 transition-all duration-400"
              style={{ opacity: phraseVisible ? 1 : 0, transform: phraseVisible ? "translateY(0)" : "translateY(6px)" }}
            >
              <div className="w-1 h-5 bg-[#ecc775] rounded-full flex-shrink-0" />
              <p className="text-white/75 text-base italic">{motivationalPhrases[phraseIdx]}</p>
            </div>
          </div>

          {/* Clock + Date */}
          <div className="text-left lg:text-right flex-shrink-0">
            <p className="text-[#ecc775] font-black text-4xl tracking-widest tabular-nums" dir="ltr">{timeStr}</p>
            <p className="text-white/60 text-sm mt-1 font-medium">{dateStr}</p>
          </div>
        </div>
      </div>

      {/* ── Stats Row ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { icon: Layers,        label: "الخدمات",   value: summary.services  || 0, color: "from-[#173269] to-[#1e4080]", accent: "#ecc775" },
          { icon: Building2,     label: "العروض العقارية",  value: summary.projects  || 0, color: "from-[#c9a34e] to-[#e6bc70]", accent: "#173269" },
          { icon: MapPin,        label: "المخططات",  value: summary.plots     || 0, color: "from-[#173269] to-[#1e4080]", accent: "#ecc775" },
          { icon: MessageSquare, label: "الرسائل",   value: summary.inquiries || 0, color: "from-[#c9a34e] to-[#e6bc70]", accent: "#173269" },
        ].map(({ icon: Icon, label, value, color, accent }) => (
          <div key={label} className={`bg-gradient-to-br ${color} rounded-sm p-6 shadow-lg flex items-center gap-5`}>
            <div className="w-14 h-14 rounded-sm flex items-center justify-center flex-shrink-0" style={{ background: `${accent}20`, border: `1.5px solid ${accent}40` }}>
              <Icon size={24} style={{ color: accent }} />
            </div>
            <div>
              <p className="font-black text-4xl leading-none mb-1" style={{ color: accent }}>{value}</p>
              <p className="text-white/70 text-sm font-semibold">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Quick Access ── */}
      <div className="bg-white rounded-sm border border-gray-100 shadow-sm p-6">
        <h3 className="text-[#173269] font-black text-base mb-5 flex items-center gap-2">
          <span className="w-1 h-5 bg-[#ecc775] rounded-full inline-block" />
          وصول سريع
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickLinks.map(({ label, tab, icon: Icon, color }) => (
            <button
              key={label}
              onClick={() => document.querySelector(`[data-testid="sidebar-tab-${tab}"]`)?.click()}
              className="flex flex-col items-center gap-2 p-4 rounded-sm border border-gray-100 hover:border-[#ecc775] hover:shadow-md transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-sm flex items-center justify-center transition-colors duration-200"
                style={{ background: `${color}15` }}>
                <Icon size={18} style={{ color }} className="group-hover:scale-110 transition-transform duration-200" />
              </div>
              <span className="text-gray-600 text-xs font-bold group-hover:text-[#173269]">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Latest Messages ── */}
      <LatestInquiries />

      {/* ── Visitor Stats ── */}
      <VisitorWidget />

    </div>
  );
};

const LatestInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  useEffect(() => {
    API.get("/admin/inquiries").then(r => setInquiries((r.data || []).slice(0, 4))).catch(() => {});
  }, []);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr);
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `منذ ${mins} دقيقة`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `منذ ${hours} ساعة`;
    return `منذ ${Math.floor(hours / 24)} يوم`;
  };

  return (
    <div className="bg-white rounded-sm border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-[#173269] font-black text-base flex items-center gap-2">
          <span className="w-1 h-5 bg-[#ecc775] rounded-full inline-block" />
          آخر الرسائل
        </h3>
        <span className="text-xs text-gray-400 font-semibold">{inquiries.length} رسالة</span>
      </div>
      {inquiries.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">لا توجد رسائل بعد</p>
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inq, i) => {
            const initials = inq.name?.charAt(0) || "؟";
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-sm hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                <div className="w-9 h-9 rounded-full bg-[#173269] text-[#ecc775] font-black text-sm flex items-center justify-center flex-shrink-0">{initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-[#173269] text-sm truncate">{inq.name}</p>
                    <p className="text-gray-400 text-xs flex-shrink-0">{timeAgo(inq.created_at)}</p>
                  </div>
                  <p className="text-gray-500 text-xs truncate mt-0.5">{inq.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ========== Visitor Widget ==========
const VISIT_PERIODS = [
  { id: "realtime", label: "الوقت الفعلي", sub: "آخر 5 دقائق" },
  { id: "7days",    label: "7 أيام",        sub: "" },
  { id: "month",    label: "الشهر",         sub: "" },
  { id: "all",      label: "الكل",          sub: "" },
];

const VisitorWidget = () => {
  const [period, setPeriod] = useState("all");
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((p) => {
    setLoading(true);
    API.get(`/admin/visitors?period=${p}`)
      .then(r => setCount(r.data.count))
      .catch(() => setCount(0))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(period); }, [period, load]);

  // Auto-refresh every 30s for real-time
  useEffect(() => {
    if (period !== "realtime") return;
    const t = setInterval(() => load("realtime"), 30000);
    return () => clearInterval(t);
  }, [period, load]);

  const switchPeriod = (p) => { setPeriod(p); load(p); };

  return (
    <div className="bg-white rounded-sm border border-gray-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-5">
        <span className="w-1 h-5 bg-[#ecc775] rounded-full inline-block" />
        <h3 className="text-[#173269] font-black text-base">زوار الموقع</h3>
        {period === "realtime" && (
          <span className="flex items-center gap-1 mr-auto">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-green-600 text-xs font-bold">مباشر</span>
          </span>
        )}
      </div>

      {/* Period tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {VISIT_PERIODS.map(p => (
          <button
            key={p.id}
            data-testid={`visitor-period-${p.id}`}
            onClick={() => switchPeriod(p.id)}
            className={`px-4 py-2 rounded-sm text-sm font-bold border transition-all duration-200 ${
              period === p.id
                ? "bg-[#173269] text-white border-[#173269]"
                : "bg-gray-50 text-gray-500 border-gray-200 hover:border-[#173269]/40"
            }`}
          >
            {p.label}
            {p.sub && <span className="block text-[10px] opacity-60">{p.sub}</span>}
          </button>
        ))}
      </div>

      {/* Count */}
      <div className="text-center py-6 bg-gradient-to-br from-[#173269]/3 to-[#ecc775]/5 rounded-sm border border-[#173269]/5">
        {loading ? (
          <div className="w-10 h-10 border-[3px] border-[#173269] border-t-transparent rounded-full animate-spin mx-auto" />
        ) : (
          <>
            <p className="font-black text-6xl text-[#173269] leading-none tabular-nums" data-testid="visitor-count">
              {count?.toLocaleString("ar-SA") ?? "—"}
            </p>
            <p className="text-gray-500 text-sm mt-3 font-semibold">
              {period === "realtime" ? "زيارة في آخر 5 دقائق" :
               period === "7days"   ? "زيارة في آخر 7 أيام"  :
               period === "month"   ? "زيارة في آخر 30 يوم"  : "إجمالي الزيارات"}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// ========== Hero Tab ==========
// ── Hero Live Preview ──
const HeroPreview = ({ data }) => {
  const DEFAULT_BG = "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=80";
  return (
    <div className="relative overflow-hidden rounded-sm shadow-2xl bg-[#173269]" style={{ aspectRatio: "16/9" }}>
      {/* Background */}
      <img
        src={data.image || DEFAULT_BG}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        onError={e => { e.target.style.display = "none"; }}
      />
      {/* Gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#173269]/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/50 to-transparent" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-5 text-right">
        {data.badge && (
          <span className="inline-block self-end bg-[#ecc775]/20 border border-[#ecc775]/40 text-[#ecc775] text-[10px] font-black px-2 py-0.5 rounded-sm mb-2">
            {data.badge}
          </span>
        )}
        {data.subtitle && (
          <p className="text-[#ecc775] text-[11px] font-bold mb-1 tracking-widest uppercase">{data.subtitle}</p>
        )}
        <h1 className="text-white font-black text-lg leading-tight mb-2 drop-shadow-lg">
          {data.title || "العنوان الرئيسي"}
        </h1>
        <p className="text-white/70 text-[11px] mb-3 line-clamp-2 leading-relaxed">
          {data.description || "الوصف سيظهر هنا"}
        </p>
        <div className="flex gap-2">
          {data.cta1_text && (
            <span className="bg-[#ecc775] text-[#173269] px-3 py-1.5 text-[10px] font-black rounded-sm">
              {data.cta1_text}
            </span>
          )}
          {data.cta2_text && (
            <span className="border border-white/50 text-white px-3 py-1.5 text-[10px] font-bold rounded-sm">
              {data.cta2_text}
            </span>
          )}
        </div>
      </div>

      {/* Live badge */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-sm">
        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
        <span className="text-white text-[10px] font-bold">معاينة مباشرة</span>
      </div>
    </div>
  );
};

const HeroTab = () => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const bgFileRef = useRef(null);

  useEffect(() => {
    API.get("/content/hero").then(r => setData(r.data.data || {}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/content/hero", { data });
      setMsg("تم الحفظ بنجاح ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const uploadBg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await API.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setData(d => ({ ...d, image: `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` }));
    } catch { setMsg("فشل رفع الصورة"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-5 pb-10">

      {/* Save bar */}
      <div className="flex items-center justify-between bg-white border border-gray-100 shadow-sm rounded-sm px-6 py-4">
        <h2 className="text-[#173269] font-black text-lg">تعديل البانر الرئيسي</h2>
        <div className="flex items-center gap-3">
          {msg && <span className={`text-sm font-bold ${msg.includes("فشل") ? "text-red-500" : "text-green-600"}`}>{msg}</span>}
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#173269] text-white px-5 py-2.5 rounded-sm font-bold text-sm hover:bg-[#0A1128] disabled:opacity-60">
            <Save size={15} />{saving ? "جاري الحفظ..." : "حفظ التغييرات"}
          </button>
        </div>
      </div>

      {/* Split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 items-start">

        {/* ── Form ── */}
        <div className="lg:col-span-3 bg-white rounded-sm border border-gray-100 shadow-sm p-6 space-y-4">
          <h3 className="font-black text-[#173269] text-sm flex items-center gap-2 mb-5">
            <span className="w-1 h-4 bg-[#ecc775] rounded-full" />النصوص والمحتوى
          </h3>
          <FormRow label="العنوان الرئيسي">
            <input data-testid="hero-title-input" value={data.title || ""} onChange={e => setData({...data, title: e.target.value})} className={inputClass} placeholder="شريكك الموثوق في عالم العقارات" />
          </FormRow>
          <FormRow label="اسم الشركة / Tagline">
            <input value={data.subtitle || ""} onChange={e => setData({...data, subtitle: e.target.value})} className={inputClass} placeholder="الوسيم العقارية" />
          </FormRow>
          <FormRow label="الوصف">
            <textarea value={data.description || ""} onChange={e => setData({...data, description: e.target.value})} className={`${inputClass} h-20 resize-none`} />
          </FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="نص الزر الأول">
              <input value={data.cta1_text || ""} onChange={e => setData({...data, cta1_text: e.target.value})} className={inputClass} />
            </FormRow>
            <FormRow label="نص الزر الثاني">
              <input value={data.cta2_text || ""} onChange={e => setData({...data, cta2_text: e.target.value})} className={inputClass} />
            </FormRow>
          </div>
          <FormRow label="نص الشارة (Badge)">
            <input value={data.badge || ""} onChange={e => setData({...data, badge: e.target.value})} className={inputClass} placeholder="موثوق • محترف • متميز" />
          </FormRow>

          <div className="border-t border-gray-100 pt-4 mt-2">
            <h3 className="font-black text-[#173269] text-sm flex items-center gap-2 mb-4">
              <span className="w-1 h-4 bg-[#ecc775] rounded-full" />صورة الخلفية
            </h3>
            <FormRow label="رابط الصورة (URL)">
              <input value={data.image || ""} onChange={e => setData({...data, image: e.target.value})} className={inputClass} dir="ltr" placeholder="https://..." />
            </FormRow>
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-gray-200" /><span className="text-gray-400 text-xs">أو</span><div className="flex-1 h-px bg-gray-200" />
            </div>
            <input ref={bgFileRef} type="file" accept="image/*" onChange={uploadBg} className="hidden" />
            <button
              onClick={() => bgFileRef.current?.click()}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#173269]/20 hover:border-[#ecc775] bg-gray-50 hover:bg-[#ecc775]/5 text-[#173269] font-bold py-3 rounded-sm text-sm transition-all disabled:opacity-60"
            >
              {uploading ? <><div className="w-3.5 h-3.5 border-2 border-[#173269] border-t-transparent rounded-full animate-spin" />جاري الرفع...</> : <>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                رفع صورة خلفية من الجهاز</>}
            </button>
          </div>
        </div>

        {/* ── Live Preview ── */}
        <div className="lg:col-span-2 sticky top-5">
          <div className="bg-white rounded-sm border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <Eye size={14} className="text-[#173269]" />
              <p className="text-[#173269] font-black text-sm">المعاينة المباشرة</p>
              <span className="text-xs text-gray-400">— تتحدث فوراً</span>
            </div>
            <HeroPreview data={data} />
            <p className="text-gray-400 text-xs mt-3 text-center">اضغط "حفظ التغييرات" لتطبيقها على الموقع</p>
          </div>
        </div>

      </div>
    </div>
  );
};

// ========== Stats Tab ==========
const StatsTab = () => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    API.get("/content/stats").then(r => {
      setItems(r.data.data?.items || [
        { value: "3", suffix: "+", label: "سنوات الخبرة", icon: "Award" },
        { value: "200", suffix: "+", label: "صفقة ناجحة", icon: "TrendingUp" },
        { value: "6", suffix: "", label: "خدمة متكاملة", icon: "Layers" },
        { value: "2", suffix: "", label: "فرع في المملكة", icon: "MapPin" },
      ]);
    });
  }, []);

  const update = (i, field, val) => {
    const arr = [...items];
    arr[i] = { ...arr[i], [field]: val };
    setItems(arr);
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/content/stats", { data: { items } });
      setMsg("تم الحفظ ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <ContentEditor title="إدارة الإحصائيات" onSave={save} saving={saving} msg={msg}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item, i) => (
          <div key={i} className="bg-gray-50 border border-gray-200 p-4 rounded-sm">
            <p className="text-[#173269] font-bold text-sm mb-3">الإحصاء {i + 1}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>القيمة</label>
                <input value={item.value} onChange={e => update(i, "value", e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>اللاحقة</label>
                <input value={item.suffix} onChange={e => update(i, "suffix", e.target.value)} className={inputClass} placeholder="+" />
              </div>
              <div className="col-span-2">
                <label className={labelClass}>التسمية</label>
                <input value={item.label} onChange={e => update(i, "label", e.target.value)} className={inputClass} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </ContentEditor>
  );
};

// ========== Services Tab ==========
const ServicesTab = () => {
  const [services, setServices] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    API.get("/admin/services").then(r => setServices(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (item) => {
    try {
      if (item.id && services.find(s => s.id === item.id)) {
        await API.put(`/admin/services/${item.id}`, item);
      } else {
        await API.post("/admin/services", item);
      }
      setMsg("تم الحفظ ✓");
      setTimeout(() => setMsg(""), 3000);
      load();
      setShowForm(false);
      setEditItem(null);
    } catch { setMsg("فشل الحفظ"); }
  };

  const del = async (id) => {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    await API.delete(`/admin/services/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black text-[#173269]">إدارة الخدمات</h2>
        <button
          data-testid="add-service-btn"
          onClick={() => { setEditItem(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-[#173269] text-white px-4 py-2.5 font-bold text-sm hover:bg-[#0A1128] transition-colors rounded-sm"
        >
          <Plus size={16} /> إضافة خدمة
        </button>
      </div>
      {msg && <p className="text-green-600 bg-green-50 border border-green-200 px-4 py-2 rounded-sm mb-4 text-sm">{msg}</p>}

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-right text-[#173269] font-bold">العنوان</th>
              <th className="px-4 py-3 text-right text-[#173269] font-bold hidden md:table-cell">الوصف</th>
              <th className="px-4 py-3 text-right text-[#173269] font-bold">الترتيب</th>
              <th className="px-4 py-3 text-center text-[#173269] font-bold">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {services.map((s, i) => (
              <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                <td className="px-4 py-3 font-semibold text-[#173269]">{s.title}</td>
                <td className="px-4 py-3 text-gray-500 hidden md:table-cell truncate max-w-xs">{s.description}</td>
                <td className="px-4 py-3 text-gray-500">{s.order}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => { setEditItem(s); setShowForm(true); }} className="p-2 text-[#173269] hover:bg-[#173269]/10 rounded-sm">
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => del(s.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-sm">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <ServiceForm
          initial={editItem}
          onSave={save}
          onClose={() => { setShowForm(false); setEditItem(null); }}
        />
      )}
    </div>
  );
};

const ServiceForm = ({ initial, onSave, onClose }) => {
  const [data, setData] = useState(initial || { title: "", description: "", icon: "Star", order: 0 });
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white w-full max-w-lg rounded-sm shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="font-black text-[#173269] text-lg">{initial ? "تعديل الخدمة" : "إضافة خدمة جديدة"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <FormRow label="العنوان">
            <input data-testid="service-title-input" value={data.title} onChange={e => setData({...data, title: e.target.value})} className={inputClass} required />
          </FormRow>
          <FormRow label="الوصف">
            <textarea value={data.description} onChange={e => setData({...data, description: e.target.value})} className={`${inputClass} h-24 resize-none`} />
          </FormRow>
          <FormRow label="الأيقونة">
            <IconPicker value={data.icon} onChange={val => setData({...data, icon: val})} />
          </FormRow>
          <FormRow label="الترتيب">
            <input type="number" value={data.order} onChange={e => setData({...data, order: +e.target.value})} className={inputClass} />
          </FormRow>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button onClick={() => onSave(data)} className="flex-1 bg-[#173269] text-white py-3 font-bold rounded-sm hover:bg-[#0A1128]">
            <Save size={16} className="inline ml-2" />حفظ
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 py-3 font-bold rounded-sm hover:bg-gray-200">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ========== Projects Tab ==========
const ProjectsTab = () => {
  const [projects, setProjects] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(() => {
    API.get("/admin/projects").then(r => setProjects(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (item) => {
    try {
      if (item.id && projects.find(p => p.id === item.id)) {
        await API.put(`/admin/projects/${item.id}`, item);
      } else {
        await API.post("/admin/projects", item);
      }
      setMsg("تم الحفظ ✓");
      setTimeout(() => setMsg(""), 3000);
      load();
      setShowForm(false);
      setEditItem(null);
    } catch { setMsg("فشل الحفظ"); }
  };

  const del = async (id) => {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    await API.delete(`/admin/projects/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-black text-[#173269]">إدارة العروض العقارية</h2>
        <button
          data-testid="add-project-btn"
          onClick={() => { setEditItem(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-[#173269] text-white px-4 py-2.5 font-bold text-sm hover:bg-[#0A1128] transition-colors rounded-sm"
        >
          <Plus size={16} /> إضافة مشروع
        </button>
      </div>
      {msg && <p className="text-green-600 bg-green-50 border border-green-200 px-4 py-2 rounded-sm mb-4 text-sm">{msg}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {projects.map((p) => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm">
            <div className="h-40 overflow-hidden">
              <img src={p.images?.[0] || p.image} alt={p.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-4">
              <p className="font-bold text-[#173269] mb-1">{p.title}</p>
              <p className="text-gray-500 text-xs mb-3">{p.location}</p>
              <div className="flex gap-2">
                <button onClick={() => { setEditItem(p); setShowForm(true); }} className="flex-1 bg-[#173269]/10 text-[#173269] py-2 text-xs font-bold rounded-sm hover:bg-[#173269]/20 flex items-center justify-center gap-1">
                  <Edit2 size={12} />تعديل
                </button>
                <button onClick={() => del(p.id)} className="flex-1 bg-red-50 text-red-600 py-2 text-xs font-bold rounded-sm hover:bg-red-100 flex items-center justify-center gap-1">
                  <Trash2 size={12} />حذف
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <ProjectForm
          initial={editItem}
          onSave={save}
          onClose={() => { setShowForm(false); setEditItem(null); }}
        />
      )}
    </div>
  );
};

const ProjectForm = ({ initial, onSave, onClose }) => {
  const [data, setData] = useState({
    title: "", description: "", location: "", price: "", type: "", badge: "", order: 0,
    ...initial,
  });
  const [imagesText, setImagesText] = useState(
    (initial?.images?.length ? initial.images : initial?.image ? [initial.image] : []).join("\n")
  );
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const fileRef = React.useRef(null);

  const handleImgUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadMsg("جاري الرفع...");
    const urls = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await API.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      } catch { setUploadMsg("فشل رفع بعض الصور"); }
    }
    if (urls.length) {
      setImagesText(prev => (prev.trim() ? prev.trim() + "\n" : "") + urls.join("\n"));
      setUploadMsg(`✓ تم رفع ${urls.length} صورة`);
      setTimeout(() => setUploadMsg(""), 4000);
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleSave = () => {
    const images = imagesText.split("\n").map(s => s.trim()).filter(Boolean);
    onSave({ ...data, images });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" dir="rtl">
      <div className="bg-white w-full max-w-lg rounded-sm shadow-2xl my-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="font-black text-[#173269] text-lg">{initial ? "تعديل المشروع" : "إضافة مشروع جديد"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {[
            { field: "title", label: "العنوان" },
            { field: "location", label: "الموقع" },
            { field: "price", label: "السعر" },
            { field: "type", label: "النوع (فيلا، شقة...)" },
            { field: "badge", label: "الشارة (مميز، جديد...)" },
          ].map(({ field, label }) => (
            <FormRow key={field} label={label}>
              <input value={data[field] || ""} onChange={e => setData({...data, [field]: e.target.value})} className={inputClass} />
            </FormRow>
          ))}
          <FormRow label="الوصف">
            <textarea value={data.description || ""} onChange={e => setData({...data, description: e.target.value})} className={`${inputClass} h-20 resize-none`} />
          </FormRow>

          {/* Images section */}
          <div>
            <label className={labelClass}>صور المشروع</label>

            {/* Upload button */}
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleImgUpload} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#173269]/20 hover:border-[#ecc775] bg-gray-50 hover:bg-[#ecc775]/5 text-[#173269] font-bold py-3 rounded-sm transition-all duration-200 mb-2 text-sm disabled:opacity-60"
            >
              {uploading ? (
                <><div className="w-3.5 h-3.5 border-2 border-[#173269] border-t-transparent rounded-full animate-spin" />جاري الرفع...</>
              ) : (
                <><svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>رفع صور من الجهاز (متعددة)</>
              )}
            </button>
            {uploadMsg && <p className="text-xs text-green-600 font-semibold mb-2">{uploadMsg}</p>}

            {/* URL textarea */}
            <textarea
              value={imagesText}
              onChange={e => setImagesText(e.target.value)}
              className={`${inputClass} h-24 resize-none`}
              dir="ltr"
              placeholder={"https://example.com/image1.jpg\nhttps://example.com/image2.jpg"}
            />
            <p className="text-gray-400 text-xs mt-1">أو أدخل روابط URL — رابط واحد في كل سطر</p>
          </div>
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button onClick={handleSave} className="flex-1 bg-[#173269] text-white py-3 font-bold rounded-sm hover:bg-[#0A1128]">
            <Save size={16} className="inline ml-2" />حفظ
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 py-3 font-bold rounded-sm hover:bg-gray-200">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

// ========== Values Tab ==========
// ========== Values Tab ==========
const ICON_OPTIONS = [
  { name: "Lightbulb", Icon: Lightbulb, label: "ابتكار" },
  { name: "Zap",       Icon: Zap,       label: "كفاءة" },
  { name: "Users",     Icon: Users,     label: "فريق" },
  { name: "Shield",    Icon: Shield,    label: "أمان" },
  { name: "Star",      Icon: Star,      label: "تميز" },
  { name: "Heart",     Icon: Heart,     label: "اهتمام" },
  { name: "Award",     Icon: Award,     label: "جودة" },
  { name: "TrendingUp",Icon: TrendingUp,label: "نمو" },
  { name: "CheckCircle",Icon:CheckCircle,label:"دقة" },
  { name: "Globe",     Icon: Globe,     label: "عالمي" },
  { name: "Handshake", Icon: Handshake, label: "شراكة" },
  { name: "Target",    Icon: Target,    label: "هدف" },
  { name: "Gem",       Icon: Gem,       label: "قيمة" },
  { name: "Key",       Icon: Key,       label: "مفتاح" },
  { name: "Leaf",      Icon: Leaf,      label: "استدامة" },
  { name: "Smile",     Icon: Smile,     label: "رضا" },
  { name: "ThumbsUp",  Icon: ThumbsUp,  label: "ثقة" },
  { name: "Briefcase", Icon: Briefcase, label: "احترافية" },
  { name: "Building2", Icon: Building2, label: "مشاريع" },
  { name: "MapPin",    Icon: MapPin,    label: "موقع" },
  { name: "Clock",     Icon: Clock,     label: "التزام" },
  { name: "Phone",     Icon: Phone,     label: "تواصل" },
];
const ICON_MAP = Object.fromEntries(ICON_OPTIONS.map(o => [o.name, o.Icon]));

const IconPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const Sel = ICON_MAP[value] || Lightbulb;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-sm bg-white hover:border-[#ecc775] transition-colors w-full"
      >
        <div className="w-7 h-7 rounded bg-[#173269]/10 flex items-center justify-center flex-shrink-0">
          <Sel size={15} className="text-[#173269]" />
        </div>
        <span className="text-sm text-gray-700 flex-1 text-right">{ICON_OPTIONS.find(o=>o.name===value)?.label || value}</span>
        <ChevronDown size={13} className="text-gray-400" />
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 right-0 bg-white border border-gray-200 rounded-sm shadow-xl p-3 w-64">
          <div className="grid grid-cols-4 gap-2">
            {ICON_OPTIONS.map(({ name, Icon, label }) => (
              <button
                key={name}
                onClick={() => { onChange(name); setOpen(false); }}
                className={`flex flex-col items-center gap-1 p-2 rounded-sm transition-all hover:bg-[#ecc775]/10 border ${
                  value === name ? "border-[#ecc775] bg-[#ecc775]/10" : "border-transparent"
                }`}
              >
                <Icon size={18} className={value === name ? "text-[#173269]" : "text-gray-500"} />
                <span className="text-[10px] text-gray-500">{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ValuesTab = () => {
  const [data, setData] = useState({ title: "", subtitle: "", description: "", values: [], timeline: [] });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    API.get("/content/values").then(r => {
      const d = r.data.data || {};
      setData({
        title: d.title || "",
        subtitle: d.subtitle || "",
        description: d.description || "",
        values: d.values || [],
        timeline: d.timeline || [],
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/content/values", { data });
      setMsg("تم الحفظ ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  // Values helpers
  const addValue = () => setData(d => ({ ...d, values: [...d.values, { title: "قيمة جديدة", description: "", icon: "Star" }] }));
  const removeValue = (i) => setData(d => ({ ...d, values: d.values.filter((_, idx) => idx !== i) }));
  const updateValue = (i, key, val) => setData(d => {
    const arr = [...d.values]; arr[i] = { ...arr[i], [key]: val }; return { ...d, values: arr };
  });
  const moveValue = (i, dir) => setData(d => {
    const arr = [...d.values]; const j = i + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...d, values: arr };
  });

  // Timeline helpers
  const addTimeline = () => setData(d => ({ ...d, timeline: [...d.timeline, { year: "2025", title: "حدث جديد", description: "" }] }));
  const removeTimeline = (i) => setData(d => ({ ...d, timeline: d.timeline.filter((_, idx) => idx !== i) }));
  const updateTimeline = (i, key, val) => setData(d => {
    const arr = [...d.timeline]; arr[i] = { ...arr[i], [key]: val }; return { ...d, timeline: arr };
  });
  const moveTimeline = (i, dir) => setData(d => {
    const arr = [...d.timeline]; const j = i + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...d, timeline: arr };
  });

  return (
    <div className="space-y-6 pb-10">

      {/* Save bar */}
      <div className="flex items-center justify-between bg-white border border-gray-100 shadow-sm rounded-sm px-6 py-4">
        <h2 className="text-[#173269] font-black text-lg">إدارة القيم والمسيرة</h2>
        <div className="flex items-center gap-3">
          {msg && <span className={`text-sm font-bold ${msg.includes("فشل") ? "text-red-500" : "text-green-600"}`}>{msg}</span>}
          <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#173269] text-white px-5 py-2.5 rounded-sm font-bold text-sm hover:bg-[#0A1128] disabled:opacity-60">
            <Save size={15} />{saving ? "جاري الحفظ..." : "حفظ التغييرات"}
          </button>
        </div>
      </div>

      {/* Section info */}
      <div className="bg-white rounded-sm border border-gray-100 shadow-sm p-6 space-y-4">
        <h3 className="font-black text-[#173269] text-base flex items-center gap-2">
          <span className="w-1 h-5 bg-[#ecc775] rounded-full" />إعدادات القسم
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormRow label="العنوان الرئيسي">
            <input value={data.title} onChange={e => setData({...data, title: e.target.value})} className={inputClass} />
          </FormRow>
          <FormRow label="العنوان الفرعي">
            <input value={data.subtitle} onChange={e => setData({...data, subtitle: e.target.value})} className={inputClass} />
          </FormRow>
        </div>
        <FormRow label="الوصف">
          <textarea value={data.description} onChange={e => setData({...data, description: e.target.value})} className={`${inputClass} h-16 resize-none`} />
        </FormRow>
      </div>

      {/* Values Cards */}
      <div className="bg-white rounded-sm border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-black text-[#173269] text-base flex items-center gap-2">
            <span className="w-1 h-5 bg-[#ecc775] rounded-full" />القيم الأساسية
            <span className="text-xs text-gray-400 font-normal">({data.values.length} قيم)</span>
          </h3>
          <button onClick={addValue} className="flex items-center gap-1.5 bg-[#ecc775] text-[#173269] px-3 py-2 rounded-sm text-sm font-black hover:bg-[#d4a93c] transition-colors">
            <Plus size={14} /> إضافة قيمة
          </button>
        </div>

        {data.values.length === 0 && (
          <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-sm">
            <Star size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">لا توجد قيم — اضغط "إضافة قيمة"</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {data.values.map((v, i) => {
            const Icon = ICON_MAP[v.icon] || Star;
            return (
              <div key={i} className="border border-gray-200 rounded-sm p-4 hover:border-[#ecc775]/50 transition-colors">
                {/* Card preview */}
                <div className="flex items-center gap-3 mb-4 p-3 bg-[#173269]/5 rounded-sm">
                  <div className="w-10 h-10 bg-[#173269] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Icon size={18} className="text-[#ecc775]" />
                  </div>
                  <div>
                    <p className="font-black text-[#173269] text-sm">{v.title || "القيمة"}</p>
                    <p className="text-gray-400 text-xs truncate max-w-[140px]">{v.description || "الوصف"}</p>
                  </div>
                </div>
                {/* Fields */}
                <div className="space-y-3">
                  <FormRow label="العنوان">
                    <input value={v.title} onChange={e => updateValue(i, "title", e.target.value)} className={inputClass} />
                  </FormRow>
                  <FormRow label="الأيقونة">
                    <IconPicker value={v.icon} onChange={val => updateValue(i, "icon", val)} />
                  </FormRow>
                  <FormRow label="الوصف">
                    <textarea value={v.description} onChange={e => updateValue(i, "description", e.target.value)} className={`${inputClass} h-14 resize-none`} />
                  </FormRow>
                </div>
                {/* Controls */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex gap-1">
                    <button onClick={() => moveValue(i, -1)} disabled={i === 0} className="p-1.5 rounded text-gray-400 hover:text-[#173269] hover:bg-gray-100 disabled:opacity-30 transition-colors"><ChevronUp size={14} /></button>
                    <button onClick={() => moveValue(i, 1)} disabled={i === data.values.length - 1} className="p-1.5 rounded text-gray-400 hover:text-[#173269] hover:bg-gray-100 disabled:opacity-30 transition-colors"><ChevronDown size={14} /></button>
                  </div>
                  <button onClick={() => removeValue(i)} className="flex items-center gap-1 text-red-400 hover:text-red-600 text-xs font-bold transition-colors">
                    <Trash2 size={13} /> حذف
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-sm border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-black text-[#173269] text-base flex items-center gap-2">
            <span className="w-1 h-5 bg-[#ecc775] rounded-full" />مسيرتنا (التايم لاين)
            <span className="text-xs text-gray-400 font-normal">({data.timeline.length} محطات)</span>
          </h3>
          <button onClick={addTimeline} className="flex items-center gap-1.5 bg-[#ecc775] text-[#173269] px-3 py-2 rounded-sm text-sm font-black hover:bg-[#d4a93c] transition-colors">
            <Plus size={14} /> إضافة محطة
          </button>
        </div>

        <div className="space-y-3">
          {data.timeline.map((t, i) => (
            <div key={i} className="flex items-start gap-3 border border-gray-200 rounded-sm p-4 hover:border-[#ecc775]/50 transition-colors">
              {/* Year badge */}
              <div className="w-14 h-14 bg-[#173269] rounded-sm flex items-center justify-center flex-shrink-0 text-[#ecc775] font-black text-sm">{t.year}</div>
              {/* Fields */}
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FormRow label="السنة">
                  <input value={t.year} onChange={e => updateTimeline(i, "year", e.target.value)} className={inputClass} dir="ltr" />
                </FormRow>
                <FormRow label="العنوان">
                  <input value={t.title} onChange={e => updateTimeline(i, "title", e.target.value)} className={inputClass} />
                </FormRow>
                <FormRow label="الوصف">
                  <input value={t.description} onChange={e => updateTimeline(i, "description", e.target.value)} className={inputClass} />
                </FormRow>
              </div>
              {/* Controls */}
              <div className="flex flex-col gap-1">
                <button onClick={() => moveTimeline(i, -1)} disabled={i === 0} className="p-1.5 rounded text-gray-400 hover:text-[#173269] hover:bg-gray-100 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => moveTimeline(i, 1)} disabled={i === data.timeline.length - 1} className="p-1.5 rounded text-gray-400 hover:text-[#173269] hover:bg-gray-100 disabled:opacity-30"><ChevronDown size={14} /></button>
                <button onClick={() => removeTimeline(i)} className="p-1.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {data.timeline.length === 0 && (
            <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-sm">
              <Clock size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">لا توجد محطات — اضغط "إضافة محطة"</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

// ========== Contact Tab ==========
const ContactTab = () => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    API.get("/content/contact").then(r => setData(r.data.data || {}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/content/contact", { data });
      setMsg("تم الحفظ ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const fields = [
    { field: "title", label: "العنوان" },
    { field: "subtitle", label: "العنوان الفرعي" },
    { field: "phone1", label: "الهاتف 1", dir: "ltr" },
    { field: "phone2", label: "الهاتف 2", dir: "ltr" },
    { field: "whatsapp", label: "واتساب (مع مفتاح الدولة)", dir: "ltr" },
    { field: "email", label: "البريد الإلكتروني", dir: "ltr" },
    { field: "address", label: "العنوان" },
    { field: "working_hours", label: "ساعات العمل" },
    { field: "social_instagram", label: "انستقرام (@username)", dir: "ltr" },
    { field: "social_twitter", label: "تويتر / X (@username)", dir: "ltr" },
    { field: "social_tiktok", label: "تيك توك (@username)", dir: "ltr" },
    { field: "social_linkedin", label: "لينكدإن (@username أو اسم الشركة)", dir: "ltr" },
  ];

  return (
    <ContentEditor title="معلومات التواصل" onSave={save} saving={saving} msg={msg}>
      {fields.map(({ field, label, dir }) => (
        <FormRow key={field} label={label}>
          <input
            value={data[field] || ""}
            onChange={e => setData({...data, [field]: e.target.value})}
            className={inputClass}
            dir={dir || "rtl"}
          />
        </FormRow>
      ))}
    </ContentEditor>
  );
};

// ========== Footer Tab ==========
const FooterTab = () => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    API.get("/content/footer").then(r => setData(r.data.data || {}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/content/footer", { data });
      setMsg("تم الحفظ ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  return (
    <ContentEditor title="تعديل الفوتر" onSave={save} saving={saving} msg={msg}>
      {[
        { field: "company_name", label: "اسم الشركة" },
        { field: "tagline", label: "الشعار الإنجليزي", dir: "ltr" },
        { field: "description", label: "الوصف" },
        { field: "phone1", label: "الهاتف 1", dir: "ltr" },
        { field: "phone2", label: "الهاتف 2", dir: "ltr" },
        { field: "address", label: "العنوان" },
        { field: "copyright", label: "حقوق النشر" },
      ].map(({ field, label, dir }) => (
        <FormRow key={field} label={label}>
          <input
            value={data[field] || ""}
            onChange={e => setData({...data, [field]: e.target.value})}
            className={inputClass}
            dir={dir || "rtl"}
          />
        </FormRow>
      ))}
    </ContentEditor>
  );
};

// ========== Inquiries Tab ==========
const STATUS_CONFIG = {
  "جديد":        { color: "bg-blue-100 text-blue-700 border-blue-200",   dot: "bg-blue-500",   order: 0 },
  "في الانتظار": { color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500",  order: 1 },
  "تم الرد":    { color: "bg-green-100 text-green-700 border-green-200",  dot: "bg-green-500",  order: 2 },
};
const STATUSES = ["جديد", "في الانتظار", "تم الرد"];

const InquiriesTab = () => {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("جديد");
  const [updating, setUpdating] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const load = () => API.get("/admin/inquiries").then(r => setInquiries(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    setUpdating(id);
    try {
      await API.put(`/admin/inquiries/${id}/status`, { status });
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status } : i));
    } catch (err) {
      const msg = err?.response?.data?.detail || "فشل تحديث الحالة";
      setErrMsg(msg);
      setTimeout(() => setErrMsg(""), 4000);
    }
    finally { setUpdating(null); }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "الآن";
    if (mins < 60) return `منذ ${mins} دقيقة`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `منذ ${hours} ساعة`;
    return new Date(dateStr).toLocaleDateString("ar-SA", { day: "numeric", month: "short" });
  };

  const cleanPhone = (phone) => {
    let p = phone?.replace(/\s+/g, "") || "";
    if (p.startsWith("0")) p = "966" + p.slice(1);
    if (!p.startsWith("966") && !p.startsWith("+")) p = "966" + p;
    return p.replace(/^\+/, "");
  };

  const counts = {
    "الكل": inquiries.length,
    "جديد": inquiries.filter(i => i.status === "جديد").length,
    "في الانتظار": inquiries.filter(i => i.status === "في الانتظار").length,
    "تم الرد": inquiries.filter(i => i.status === "تم الرد").length,
  };

  const filtered = filter === "الكل" ? inquiries : inquiries.filter(i => i.status === filter);

  return (
    <div>
      {/* ── Error Banner ── */}
      {errMsg && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm font-semibold text-center">
          {errMsg}
        </div>
      )}

      {/* ── Stats Bar ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: "إجمالي الرسائل", value: counts["الكل"],        bg: "from-[#173269] to-[#1e4080]", accent: "#ecc775" },
          { label: "جديد",           value: counts["جديد"],         bg: "from-blue-600 to-blue-700",   accent: "#fff" },
          { label: "في الانتظار",    value: counts["في الانتظار"],  bg: "from-amber-500 to-amber-600", accent: "#fff" },
          { label: "تم الرد",        value: counts["تم الرد"],      bg: "from-green-600 to-green-700", accent: "#fff" },
        ].map(({ label, value, bg, accent }) => (
          <div key={label} className={`bg-gradient-to-br ${bg} rounded-sm p-4 shadow-sm text-center`}>
            <p className="font-black text-3xl leading-none mb-1" style={{ color: accent }}>{value}</p>
            <p className="text-white/75 text-xs font-semibold">{label}</p>
          </div>
        ))}
      </div>

      {/* ── Filter Tabs ── */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[...STATUSES, "الكل"].map(tab => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-2 rounded-sm text-sm font-bold border transition-all duration-200 ${
              filter === tab
                ? "bg-[#173269] text-white border-[#173269]"
                : "bg-white text-gray-500 border-gray-200 hover:border-[#173269]/40"
            }`}
          >
            {tab}
            <span className={`mr-1.5 text-xs px-1.5 py-0.5 rounded-full ${filter === tab ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"}`}>
              {counts[tab] ?? inquiries.length}
            </span>
          </button>
        ))}
      </div>

      {/* ── List ── */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 p-16 text-center rounded-sm shadow-sm">
          <MessageSquare size={48} className="text-gray-200 mx-auto mb-4" />
          <p className="text-gray-400 font-semibold">لا توجد رسائل في هذه المرحلة</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((inq, i) => {
            const sc = STATUS_CONFIG[inq.status] || STATUS_CONFIG["جديد"];
            const wa = cleanPhone(inq.phone);
            const waMsg = encodeURIComponent(`مرحباً ${inq.name}، شكراً على تواصلك مع الوسيم العقارية.`);
            const initials = inq.name?.slice(0, 2) || "؟";
            return (
              <div
                key={inq.id || i}
                data-testid={`inquiry-${i}`}
                className="bg-white rounded-sm shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 overflow-hidden"
              >
                <div className="h-0.5 bg-gradient-to-r from-[#ecc775] to-transparent" />
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#173269] to-[#1e4080] text-[#ecc775] font-black text-base flex items-center justify-center flex-shrink-0 shadow-md">
                      {initials}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Name + time + status badge */}
                      <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                        <div>
                          <p className="font-black text-[#173269] text-base">{inq.name}</p>
                          <p className="text-[#c9a34e] text-sm font-bold" dir="ltr">{inq.phone}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-black border ${sc.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                            {inq.status}
                          </span>
                          <span className="text-gray-400 text-xs bg-gray-50 px-2 py-1 rounded-sm">{timeAgo(inq.created_at)}</span>
                        </div>
                      </div>

                      {/* Message */}
                      <div className="bg-gray-50 rounded-sm p-3 border border-gray-100 mb-4">
                        <p className="text-gray-700 text-sm leading-relaxed">{inq.message}</p>
                      </div>

                      {/* Bottom row: status change + contact buttons */}
                      <div className="flex items-center justify-between gap-3 flex-wrap">

                        {/* Status pipeline */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-gray-400 text-xs font-semibold ml-1">تغيير المرحلة:</span>
                          {STATUSES.map(s => (
                            <button
                              key={s}
                              disabled={inq.status === s || updating === inq.id}
                              onClick={() => changeStatus(inq.id, s)}
                              className={`px-2.5 py-1 rounded-sm text-xs font-bold border transition-all duration-200 ${
                                inq.status === s
                                  ? `${STATUS_CONFIG[s].color} cursor-default`
                                  : "bg-white text-gray-500 border-gray-200 hover:border-[#173269]/40 hover:text-[#173269]"
                              } disabled:opacity-60`}
                            >
                              {updating === inq.id && inq.status !== s ? "..." : s}
                            </button>
                          ))}
                        </div>

                        {/* Contact buttons */}
                        <div className="flex gap-2">
                          <a
                            href={`https://wa.me/${wa}?text=${waMsg}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`whatsapp-${i}`}
                            className="flex items-center gap-1.5 bg-[#25D366] text-white px-3 py-1.5 rounded-sm text-xs font-black hover:bg-[#20bd5a] transition-colors"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.553 4.116 1.52 5.847L.057 23.743a.75.75 0 00.917.914l5.845-1.484A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.943 0-3.761-.525-5.319-1.44l-.38-.223-3.468.881.887-3.43-.243-.394A9.956 9.956 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
                            واتساب
                          </a>
                          <a
                            href={`tel:${inq.phone}`}
                            data-testid={`call-${i}`}
                            className="flex items-center gap-1.5 bg-[#173269] text-white px-3 py-1.5 rounded-sm text-xs font-black hover:bg-[#0A1128] transition-colors"
                          >
                            <Phone size={12} />
                            اتصال
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ========== Plots Tab ==========
const PC_FIELDS = [
  { key: "page_title",       label: "عنوان الصفحة",              group: "الصفحة الرئيسية" },
  { key: "page_subtitle",    label: "الوصف المختصر",             group: "الصفحة الرئيسية" },
  { key: "region_badge",     label: "شارة المنطقة",              group: "الصفحة الرئيسية" },
  { key: "stat_plots_label", label: "تسمية إحصاء المخططات",      group: "إحصائيات البطل" },
  { key: "stat_region_label",label: "تسمية إحصاء المنطقة",       group: "إحصائيات البطل" },
  { key: "total_plots_label",label: "تسمية عدد القطع",           group: "بطاقة المخطط" },
  { key: "area_label",       label: "تسمية المساحة",             group: "بطاقة المخطط" },
  { key: "price_label",      label: "تسمية السعر",               group: "بطاقة المخطط" },
  { key: "contact_btn",      label: "نص زر التواصل",             group: "بطاقة المخطط" },
  { key: "view_pdf_btn",     label: "نص زر المخطط",              group: "بطاقة المخطط" },
  { key: "cta_title",        label: "عنوان قسم الدعوة",           group: "قسم الدعوة" },
  { key: "cta_subtitle",     label: "وصف قسم الدعوة",            group: "قسم الدعوة" },
  { key: "cta_whatsapp_btn", label: "نص زر واتساب",              group: "قسم الدعوة" },
  { key: "cta_form_btn",     label: "نص زر نموذج التواصل",       group: "قسم الدعوة" },
  { key: "preview_badge",    label: "شارة قسم الصفحة الرئيسية",  group: "معاينة الصفحة الرئيسية" },
  { key: "preview_title",    label: "عنوان القسم في الصفحة الرئيسية", group: "معاينة الصفحة الرئيسية" },
  { key: "preview_subtitle", label: "وصف القسم في الصفحة الرئيسية",  group: "معاينة الصفحة الرئيسية" },
  { key: "preview_btn",      label: "نص زر عرض الكل",           group: "معاينة الصفحة الرئيسية" },
];

const PlotsTab = () => {
  const [plots, setPlots]           = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [editPlot, setEditPlot]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [msg, setMsg]               = useState({ text: "", type: "" });
  const [uploadingImg, setUpImg]    = useState(false);
  const [uploadingPdf, setUpPdf]    = useState(false);
  const [view, setView]             = useState("list"); // "list" | "content"
  const [pcForm, setPcForm]         = useState({});
  const [pcSaving, setPcSaving]     = useState(false);
  const [pcMsg, setPcMsg]           = useState({ text: "", type: "" });
  const imgRef = useRef(null);
  const pdfRef = useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const blank = { name:"", location:"", total_plots:"", plot_area:"", price_from:"", description:"", images:[], pdf_url:"", status:"متاح", badge:"", order:0 };
  const [form, setForm] = useState(blank);

  const load = () => API.get("/admin/plots").then(r => setPlots(r.data || [])).catch(() => {});
  const loadPc = () => API.get("/admin/content/plots_page").then(r => setPcForm(r.data.data || {})).catch(() =>
    API.get("/content/plots_page").then(r => setPcForm(r.data.data || {})).catch(() => {}));

  useEffect(() => { load(); loadPc(); }, []);

  const openAdd  = ()  => { setForm(blank); setEditPlot(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ ...p, total_plots: p.total_plots ?? "" }); setEditPlot(p); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditPlot(null); setForm(blank); };

  const fUrl = (u) => (!u ? null : u.startsWith("/api/") ? `${BACKEND}${u}` : u);

  const uploadImg = async (file) => {
    setUpImg(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await API.post("/admin/upload", fd);
      const url = fUrl(res.data.url) || res.data.url;
      setForm(prev => ({ ...prev, images: [...prev.images, url] }));
    } catch { setMsg({ text: "فشل رفع الصورة", type: "error" }); }
    finally { setUpImg(false); }
  };

  const uploadPdf = async (file) => {
    setUpPdf(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await API.post("/admin/upload-pdf", fd);
      const url = fUrl(res.data.url) || res.data.url;
      setForm(prev => ({ ...prev, pdf_url: url }));
      setMsg({ text: "تم رفع الملف ✓", type: "success" });
      setTimeout(() => setMsg({ text:"", type:"" }), 3000);
    } catch { setMsg({ text: "فشل رفع الملف", type: "error" }); }
    finally { setUpPdf(false); }
  };

  const removeImg = (idx) => setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name.trim()) { setMsg({ text: "يرجى إدخال اسم المخطط", type: "error" }); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_plots: form.total_plots !== "" ? parseInt(form.total_plots) || null : null,
        order:       parseInt(form.order) || 0,
      };
      if (editPlot) { await API.put(`/admin/plots/${editPlot.id}`, payload); }
      else          { await API.post("/admin/plots", payload); }
      setMsg({ text: "تم الحفظ ✓", type: "success" });
      setTimeout(() => setMsg({ text:"", type:"" }), 3000);
      load(); closeForm();
    } catch { setMsg({ text: "فشل الحفظ", type: "error" }); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    try { await API.delete(`/admin/plots/${id}`); load(); } catch {}
  };

  const savePc = async () => {
    setPcSaving(true);
    try {
      await API.put("/admin/content/plots_page", { data: pcForm });
      setPcMsg({ text: "تم حفظ محتوى الصفحة ✓", type: "success" });
      setTimeout(() => setPcMsg({ text:"", type:"" }), 3000);
    } catch { setPcMsg({ text: "فشل الحفظ", type: "error" }); }
    finally { setPcSaving(false); }
  };

  const SC = { "متاح": "bg-green-100 text-green-700", "محجوز": "bg-amber-100 text-amber-700", "مباع": "bg-red-100 text-red-700" };

  // Group PC fields
  const groups = [...new Set(PC_FIELDS.map(f => f.group))];

  return (
    <div>
      {/* View Toggle */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-sm w-fit mb-6">
        {[{ id:"list", label:"قائمة المخططات" }, { id:"content", label:"محتوى الصفحة" }].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`px-4 py-2 rounded-sm text-sm font-bold transition-all duration-200 ${
              view === v.id ? "bg-[#173269] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
            {v.label}
          </button>
        ))}
      </div>

      {/* ── List View ── */}
      {view === "list" && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-black text-[#173269] text-xl">مخططات جازان</h2>
              <p className="text-gray-500 text-sm mt-0.5">{plots.length} مخطط مضاف</p>
            </div>
            <button onClick={openAdd} data-testid="add-plot-btn" className="flex items-center gap-2 bg-[#173269] text-white px-5 py-2.5 rounded-sm font-bold text-sm hover:bg-[#0A1128] transition-colors shadow-lg shadow-[#173269]/20">
              <Plus size={16} />إضافة مخطط
            </button>
          </div>

          {msg.text && !showForm && (
            <div className={`mb-4 p-3 rounded-sm text-sm font-semibold text-center border ${msg.type === "success" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
              {msg.text}
            </div>
          )}

          {plots.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-sm p-20 text-center">
              <MapPin size={52} className="text-gray-200 mx-auto mb-4" />
              <p className="text-gray-400 font-black text-lg">لا توجد مخططات بعد</p>
              <p className="text-gray-300 text-sm mt-1">اضغط "إضافة مخطط" لإضافة أول مخطط</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plots.map((plot, i) => {
                const img = fUrl(plot.images?.[0]);
                return (
                  <div key={plot.id || i} className="bg-white rounded-sm border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    {img ? (
                      <img src={img} alt={plot.name} className="w-full h-40 object-cover" />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-[#173269] to-[#1e4080] flex items-center justify-center">
                        <MapPin size={36} className="text-[#ecc775]" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-black text-[#173269] text-sm leading-tight">{plot.name}</h3>
                        <span className={`text-xs font-black px-2 py-0.5 rounded-sm flex-shrink-0 ${SC[plot.status] || SC["متاح"]}`}>{plot.status}</span>
                      </div>
                      {plot.location && <p className="text-gray-500 text-xs mb-2 flex items-center gap-1"><MapPin size={10} />{plot.location}</p>}
                      <div className="flex gap-1.5 flex-wrap mb-3">
                        {plot.total_plots && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-sm text-xs">{plot.total_plots} قطعة</span>}
                        {plot.plot_area   && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-sm text-xs">{plot.plot_area}</span>}
                        {plot.price_from  && <span className="bg-[#ecc775]/15 text-[#173269] px-2 py-0.5 rounded-sm text-xs font-bold">{plot.price_from}</span>}
                        {plot.pdf_url     && <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded-sm text-xs flex items-center gap-0.5"><FileText size={9} />PDF</span>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(plot)} className="flex-1 flex items-center justify-center gap-1.5 text-[#173269] border border-[#173269]/20 rounded-sm py-1.5 text-xs font-bold hover:bg-[#173269]/5 transition-colors">
                          <Edit2 size={11} />تعديل
                        </button>
                        <button onClick={() => del(plot.id)} className="flex-1 flex items-center justify-center gap-1.5 text-red-500 border border-red-100 rounded-sm py-1.5 text-xs font-bold hover:bg-red-50 transition-colors">
                          <Trash2 size={11} />حذف
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) closeForm(); }}>
              <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                  <h3 className="font-black text-[#173269] text-lg">{editPlot ? "تعديل المخطط" : "إضافة مخطط جديد"}</h3>
                  <button onClick={closeForm} className="p-2 text-gray-400 hover:text-gray-700 rounded-sm hover:bg-gray-100"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4">
                  {/* Name + Status */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">اسم المخطط *</label>
                      <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputClass} placeholder="مخطط 7 - جازان" />
                    </div>
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">الحالة</label>
                      <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className={inputClass}>
                        <option value="متاح">متاح</option>
                        <option value="محجوز">محجوز</option>
                        <option value="مباع">مباع</option>
                      </select>
                    </div>
                  </div>
                  {/* Location + Badge */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">الموقع</label>
                      <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className={inputClass} placeholder="جازان - حي المحمدية" />
                    </div>
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">الشارة (اختياري)</label>
                      <input value={form.badge} onChange={e => setForm({...form, badge: e.target.value})} className={inputClass} placeholder="جديد / حصري" />
                    </div>
                  </div>
                  {/* total_plots + area + price + order */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">إجمالي القطع</label>
                      <input type="number" value={form.total_plots} onChange={e => setForm({...form, total_plots: e.target.value})} className={inputClass} placeholder="120" />
                    </div>
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">مساحة القطعة</label>
                      <input value={form.plot_area} onChange={e => setForm({...form, plot_area: e.target.value})} className={inputClass} placeholder="400 م²" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">متوسط سعر المتر</label>
                      <input value={form.price_from} onChange={e => setForm({...form, price_from: e.target.value})} className={inputClass} placeholder="1,200 ريال/م²" />
                    </div>
                    <div>
                      <label className="block text-[#173269] font-bold text-sm mb-1">الترتيب</label>
                      <input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} className={inputClass} placeholder="0" />
                    </div>
                  </div>
                  {/* Description */}
                  <div>
                    <label className="block text-[#173269] font-bold text-sm mb-1">الوصف</label>
                    <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className={`${inputClass} h-20 resize-none`} placeholder="وصف مختصر للمخطط..." />
                  </div>
                  {/* Images */}
                  <div>
                    <label className="block text-[#173269] font-bold text-sm mb-2">الصور <span className="text-gray-400 font-normal">({form.images.length} صورة)</span></label>
                    <div className="flex flex-wrap gap-2">
                      {form.images.map((url, idx) => (
                        <div key={idx} className="relative w-20 h-20 rounded-sm overflow-hidden border border-gray-200 flex-shrink-0">
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          <button onClick={() => removeImg(idx)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
                            <X size={10} />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => imgRef.current?.click()} disabled={uploadingImg}
                        className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-sm flex flex-col items-center justify-center text-gray-400 hover:border-[#173269]/40 hover:text-[#173269] transition-colors flex-shrink-0">
                        {uploadingImg ? <div className="w-5 h-5 border-2 border-[#173269] border-t-transparent rounded-full animate-spin" /> : <Plus size={22} />}
                        <span className="text-xs mt-1">{uploadingImg ? "..." : "صورة"}</span>
                      </button>
                      <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) { uploadImg(e.target.files[0]); e.target.value = ""; } }} />
                    </div>
                  </div>
                  {/* PDF */}
                  <div>
                    <label className="block text-[#173269] font-bold text-sm mb-2">ملف المخطط (PDF)</label>
                    {form.pdf_url && (
                      <div className="flex items-center gap-2 mb-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2">
                        <FileText size={16} className="text-blue-600" />
                        <span className="text-sm text-blue-700 flex-1 truncate font-semibold">ملف PDF محمّل بنجاح</span>
                        <button onClick={() => setForm({...form, pdf_url:""})} className="text-red-400 hover:text-red-600 p-0.5"><X size={14} /></button>
                      </div>
                    )}
                    <button onClick={() => pdfRef.current?.click()} disabled={uploadingPdf}
                      className="flex items-center gap-2 text-sm text-[#173269] border border-[#173269]/20 rounded-sm px-4 py-2.5 hover:bg-[#173269]/5 transition-colors font-bold">
                      {uploadingPdf ? <div className="w-4 h-4 border-2 border-[#173269] border-t-transparent rounded-full animate-spin" /> : <Plus size={14} />}
                      {uploadingPdf ? "جاري الرفع..." : form.pdf_url ? "استبدال الملف" : "رفع ملف PDF"}
                    </button>
                    <input ref={pdfRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={e => { if (e.target.files[0]) { uploadPdf(e.target.files[0]); e.target.value = ""; } }} />
                  </div>
                  {msg.text && (
                    <div className={`p-3 rounded-sm text-sm font-semibold text-center border ${msg.type === "success" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                      {msg.text}
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button onClick={save} disabled={saving} className="flex-1 bg-[#173269] text-white font-black py-3 rounded-sm hover:bg-[#0A1128] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-[#173269]/20">
                      {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
                      {saving ? "جاري الحفظ..." : "حفظ المخطط"}
                    </button>
                    <button onClick={closeForm} className="px-8 border border-gray-200 text-gray-500 font-bold py-3 rounded-sm hover:bg-gray-50 transition-colors">إلغاء</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Content View ── */}
      {view === "content" && (
        <div>
          <div className="mb-6">
            <h2 className="font-black text-[#173269] text-xl">محتوى صفحة المخططات</h2>
            <p className="text-gray-500 text-sm mt-0.5">تحكم بجميع النصوص والعبارات التي تظهر في صفحة مخططات جازان</p>
          </div>

          {pcMsg.text && (
            <div className={`mb-5 p-3 rounded-sm text-sm font-semibold text-center border ${pcMsg.type === "success" ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
              {pcMsg.text}
            </div>
          )}

          <div className="space-y-6">
            {groups.map(group => (
              <div key={group} className="bg-white border border-gray-100 rounded-sm shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                  <span className="w-1 h-4 bg-[#ecc775] rounded-full" />
                  <h3 className="font-black text-[#173269] text-sm">{group}</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PC_FIELDS.filter(f => f.group === group).map(f => (
                    <div key={f.key}>
                      <label className="block text-gray-600 font-bold text-xs mb-1">{f.label}</label>
                      <input
                        value={pcForm[f.key] || ""}
                        onChange={e => setPcForm({ ...pcForm, [f.key]: e.target.value })}
                        className={inputClass}
                        placeholder={f.label}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <button onClick={savePc} disabled={pcSaving}
              className="flex items-center gap-2 bg-[#173269] text-white font-black px-8 py-3 rounded-sm hover:bg-[#0A1128] transition-colors disabled:opacity-50 shadow-lg shadow-[#173269]/20">
              {pcSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
              {pcSaving ? "جاري الحفظ..." : "حفظ محتوى الصفحة"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
// ========== Users Tab ==========
const UsersTab = () => {
  const { user: me, refreshUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null); // for password change
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [newPw, setNewPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "" });
  const [adding, setAdding] = useState(false);

  const [editingName, setEditingName] = useState(null); // {email, name}

  const load = () => API.get("/admin/users").then(r => setUsers(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const flash = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 3500);
  };

  const saveName = async () => {
    if (!editingName?.name?.trim()) return;
    try {
      await API.put(`/admin/users/${editingName.email}/name`, { name: editingName.name });
      flash("تم تحديث الاسم");
      setUsers(prev => prev.map(u => u.email === editingName.email ? {...u, name: editingName.name} : u));
      setEditingName(null);
      if (editingName.email === me?.email) refreshUser();
    } catch { flash("فشل التحديث", "error"); }
  };

  const addUser = async () => {
    if (!newUser.email || !newUser.password) return flash("البريد وكلمة المرور مطلوبان", "error");
    setAdding(true);
    try {
      await API.post("/admin/users", newUser);
      flash("تم إضافة المستخدم بنجاح");
      setNewUser({ email: "", password: "", name: "" });
      setShowForm(false);
      load();
    } catch (e) { flash(e.response?.data?.detail || "فشل الإضافة", "error"); }
    finally { setAdding(false); }
  };

  const changePw = async () => {
    if (!newPw || newPw.length < 6) return flash("كلمة المرور يجب أن تكون 6 أحرف على الأقل", "error");
    setPwLoading(true);
    try {
      await API.put(`/admin/users/${editUser.email}/password`, { password: newPw });
      flash("تم تحديث كلمة المرور");
      setEditUser(null); setNewPw("");
    } catch { flash("فشل التحديث", "error"); }
    finally { setPwLoading(false); }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`هل تريد حذف المستخدم ${email}؟`)) return;
    try {
      await API.delete(`/admin/users/${email}`);
      flash("تم الحذف");
      load();
    } catch (e) { flash(e.response?.data?.detail || "فشل الحذف", "error"); }
  };

  return (
    <div className="space-y-5 pb-10">

      {/* Header */}
      <div className="flex items-center justify-between bg-white border border-gray-100 shadow-sm rounded-sm px-6 py-4">
        <div>
          <h2 className="text-[#173269] font-black text-lg">إدارة المستخدمين</h2>
          <p className="text-gray-400 text-xs mt-0.5">{users.length} مستخدم مسجل</p>
        </div>
        <button
          data-testid="add-user-btn"
          onClick={() => setShowForm(v => !v)}
          className="flex items-center gap-2 bg-[#173269] text-white px-4 py-2.5 rounded-sm font-bold text-sm hover:bg-[#0A1128]"
        >
          <Plus size={15} /> إضافة مستخدم
        </button>
      </div>

      {msg.text && (
        <div className={`px-4 py-3 rounded-sm text-sm font-bold ${msg.type === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}>
          {msg.text}
        </div>
      )}

      {/* Add User Form */}
      {showForm && (
        <div className="bg-white border border-[#ecc775]/40 rounded-sm p-6 shadow-sm">
          <h3 className="font-black text-[#173269] text-base mb-4 flex items-center gap-2">
            <Plus size={16} className="text-[#ecc775]" /> مستخدم جديد
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <FormRow label="الاسم">
              <input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className={inputClass} placeholder="مدير الموقع" />
            </FormRow>
            <FormRow label="البريد الإلكتروني">
              <input value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className={inputClass} dir="ltr" placeholder="admin@example.com" type="email" />
            </FormRow>
            <FormRow label="كلمة المرور">
              <input value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} className={inputClass} type="password" placeholder="6 أحرف على الأقل" />
            </FormRow>
          </div>
          <div className="flex gap-3">
            <button onClick={addUser} disabled={adding} className="flex items-center gap-2 bg-[#173269] text-white px-5 py-2.5 rounded-sm font-bold text-sm hover:bg-[#0A1128] disabled:opacity-60">
              <Save size={14} />{adding ? "جاري الحفظ..." : "حفظ المستخدم"}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-sm font-bold text-sm hover:bg-gray-200">إلغاء</button>
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-sm border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#173269]/5 border-b border-gray-200">
            <tr>
              <th className="px-5 py-3 text-right text-[#173269] font-black">المستخدم</th>
              <th className="px-5 py-3 text-right text-[#173269] font-black hidden sm:table-cell">البريد</th>
              <th className="px-5 py-3 text-right text-[#173269] font-black hidden md:table-cell">تاريخ الإنشاء</th>
              <th className="px-5 py-3 text-center text-[#173269] font-black">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr key={u.email} className={`border-b border-gray-50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#173269] text-[#ecc775] font-black text-sm flex items-center justify-center flex-shrink-0">
                      {(u.name || u.email).charAt(0).toUpperCase()}
                    </div>
                    <div>
                      {editingName?.email === u.email ? (
                        <div className="flex items-center gap-2">
                          <input
                            autoFocus
                            value={editingName.name}
                            onChange={e => setEditingName({...editingName, name: e.target.value})}
                            onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(null); }}
                            className="border border-[#ecc775] rounded-sm px-2 py-1 text-sm font-bold text-[#173269] w-32 focus:outline-none"
                          />
                          <button onClick={saveName} className="text-green-600 hover:text-green-700"><Save size={13} /></button>
                          <button onClick={() => setEditingName(null)} className="text-gray-400 hover:text-gray-600"><X size={13} /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 group">
                          <p className="font-bold text-[#173269] text-sm">{u.name || "—"}</p>
                          <button
                            onClick={() => setEditingName({ email: u.email, name: u.name || "" })}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-[#173269] transition-opacity"
                            title="تعديل الاسم"
                          >
                            <Edit2 size={11} />
                          </button>
                        </div>
                      )}
                      {u.email === me?.email && <span className="text-[10px] text-[#ecc775] bg-[#173269] px-1.5 py-0.5 rounded font-bold">أنت</span>}
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-gray-500 text-sm hidden sm:table-cell" dir="ltr">{u.email}</td>
                <td className="px-5 py-4 text-gray-400 text-xs hidden md:table-cell">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("ar-SA") : "—"}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => { setEditUser(u); setNewPw(""); }}
                      className="flex items-center gap-1 text-xs font-bold text-[#173269] bg-[#173269]/10 px-3 py-1.5 rounded-sm hover:bg-[#173269]/20"
                    >
                      <Edit2 size={12} /> كلمة المرور
                    </button>
                    {u.email !== me?.email && (
                      <button
                        onClick={() => deleteUser(u.email)}
                        className="flex items-center gap-1 text-xs font-bold text-red-500 bg-red-50 px-3 py-1.5 rounded-sm hover:bg-red-100"
                      >
                        <Trash2 size={12} /> حذف
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Change Password Modal */}
      {editUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
          <div className="bg-white w-full max-w-sm rounded-sm shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="font-black text-[#173269]">تغيير كلمة المرور</h3>
              <button onClick={() => setEditUser(null)}><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-500 text-sm" dir="ltr">{editUser.email}</p>
              <FormRow label="كلمة المرور الجديدة">
                <input
                  type="password"
                  value={newPw}
                  onChange={e => setNewPw(e.target.value)}
                  className={inputClass}
                  placeholder="6 أحرف على الأقل"
                  data-testid="new-password-input"
                />
              </FormRow>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button onClick={changePw} disabled={pwLoading} className="flex-1 bg-[#173269] text-white py-3 font-bold rounded-sm hover:bg-[#0A1128] disabled:opacity-60">
                <Save size={14} className="inline ml-1" />{pwLoading ? "جاري..." : "تحديث"}
              </button>
              <button onClick={() => setEditUser(null)} className="flex-1 bg-gray-100 text-gray-600 py-3 font-bold rounded-sm">إلغاء</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ========== Site Settings Tab ==========
const SiteSettingsTab = () => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    API.get("/content/settings").then(r => setData(r.data.data || {}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/content/settings", { data });
      setMsg("تم الحفظ بنجاح ✓");
      setTimeout(() => setMsg(""), 3000);
    } catch { setMsg("فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMsg("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await API.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setData(prev => ({ ...prev, logo_url: fullUrl }));
      setMsg("تم رفع الشعار بنجاح ✓ — اضغط حفظ لتطبيقه");
      setTimeout(() => setMsg(""), 5000);
    } catch (err) {
      setMsg("فشل رفع الصورة — تأكد من الحجم والنوع");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <ContentEditor title="إعدادات الموقع" onSave={save} saving={saving} msg={msg}>

      {/* Logo URL */}
      <FormRow label="رابط الشعار (Logo URL)">
        <input
          data-testid="logo-url-input"
          value={data.logo_url || ""}
          onChange={e => setData({ ...data, logo_url: e.target.value })}
          className={inputClass}
          dir="ltr"
          placeholder="https://example.com/logo.png"
        />
      </FormRow>

      {/* Divider */}
      <div className="flex items-center gap-3 my-1">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-gray-400 text-xs font-bold">أو</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {/* Upload from device */}
      <FormRow label="رفع الشعار من الجهاز">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileUpload}
          className="hidden"
          data-testid="logo-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="logo-upload-btn"
          className="w-full flex items-center justify-center gap-3 border-2 border-dashed border-[#173269]/30 hover:border-[#ecc775] bg-gray-50 hover:bg-[#ecc775]/5 text-[#173269] font-bold py-4 rounded-sm transition-all duration-300 disabled:opacity-60"
        >
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-[#173269] border-t-transparent rounded-full animate-spin" />
              جاري الرفع...
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              اختر صورة من جهازك (JPG, PNG, WEBP — حتى 5MB)
            </>
          )}
        </button>
      </FormRow>

      {/* Logo Preview */}
      {data.logo_url && (
        <div>
          <label className={labelClass}>معاينة الشعار</label>
          <div className="inline-flex items-center bg-[#173269] p-4 rounded-sm mt-1">
            <img
              src={data.logo_url}
              alt="Logo Preview"
              className="h-16 w-auto object-contain"
              style={{ mixBlendMode: "screen" }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
      )}

    </ContentEditor>
  );
};

// ========== Shared Components ==========
const ContentEditor = ({ title, children, onSave, saving, msg }) => (
  <div>
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-xl font-black text-[#173269]">{title}</h2>
      <button
        data-testid="save-content-btn"
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-2 bg-[#173269] text-white px-6 py-2.5 font-bold text-sm hover:bg-[#0A1128] transition-colors disabled:opacity-60 rounded-sm"
      >
        <Save size={16} />
        {saving ? "جاري الحفظ..." : "حفظ التغييرات"}
      </button>
    </div>
    {msg && <p className={`px-4 py-2 rounded-sm mb-4 text-sm font-semibold ${msg.includes("✓") ? "text-green-700 bg-green-50 border border-green-200" : "text-red-700 bg-red-50 border border-red-200"}`}>{msg}</p>}
    <div className="bg-white border border-gray-200 p-6 rounded-sm space-y-5">
      {children}
    </div>
  </div>
);

const FormRow = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const inputClass = "w-full border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:border-[#173269] transition-colors rounded-sm font-cairo text-gray-800 bg-white";
const labelClass = "block text-gray-600 text-xs font-bold mb-1.5 uppercase tracking-wider";

export default AdminDashboard;
